import { NgModule } from "@angular/core";
import { PreloadAllModules, Router, RouterModule, Routes, UrlSegment } from "@angular/router";

import { AuthGuard, InstallationGuard, MenuBegraensningGuard, SequentialGuards, LogoutRedirectGuard } from "@e-forsyning/common/auth";
import { isTeknikerContext } from "@e-forsyning/common/util";
import { DefaultComponent } from "@e-forsyning/features/default";
import { GlemtKodeordComponent, LoginComponent, LoginTechnician, TeknikerSkiftKodeordComponent } from "@e-forsyning/features/login";

function teknikerRootMatcher(segments: UrlSegment[]) {
    if (segments.length > 0) {
        return null;
    }

    if (!isTeknikerContext()) {
        return null;
    }

    return { consumed: [] };
}

function teknikerLoginMatcher(segments: UrlSegment[]) {
    if (segments.length !== 1 || segments[0].path !== "login") {
        return null;
    }

    if (!isTeknikerContext()) {
        return null;
    }

    return { consumed: segments };
}

const routes: Routes = [
    {
        matcher: teknikerRootMatcher,
        redirectTo: "login",
        pathMatch: "full"
    },
    {
        matcher: teknikerLoginMatcher,
        component: LoginTechnician,
        data: {
            title: "Tekniker login"
        }
    },
    {
        path: "",
        component: DefaultComponent
    },
    {
        path: "login",
        data: {
            title: "Log ind"
        },
        children: [
            {
                path: "**",
                component: LoginComponent
            }
        ]
    },
    {
        path: "tekniker-login",
        data: {
            title: "Tekniker log ind"
        },
        children: [
            {
                path: "**",
                component: LoginTechnician
            }
        ]
    },
    {
        path: "logout",
        canActivate: [LogoutRedirectGuard],
        component: DefaultComponent
    },
    {
        path: "glemt-kodeord/:guid",
        component: GlemtKodeordComponent
    },
    {
        path: "skift-kodeord",
        component: TeknikerSkiftKodeordComponent,
        canActivate: [AuthGuard]
    },
    {
        path: "deeplink",
        loadChildren: () => import("@e-forsyning/features/deeplink").then((module) => module.EForsyningFeaturesDeeplinkModule)
    },
    {
        path: "silent-login",
        loadChildren: () => import("@e-forsyning/features/silent-login").then((module) => module.EForsyningFeaturesSilentLoginModule)
    },
    {
        path: "install-picker",
        loadChildren: () => import("@e-forsyning/features/installationer").then((module) => module.EForsyningFeaturesInstallationerModule),
        canActivate: [AuthGuard]
    },
    {
        path: "noter",
        loadChildren: () => import("@e-forsyning/features/noter").then((module) => module.EForsyningFeaturesNoterModule),
        canActivate: [SequentialGuards],
        data: {
            sequentialGuards: [AuthGuard, MenuBegraensningGuard, InstallationGuard]
        }
    },
    {
        path: "opgaver",
        loadChildren: () => import("@e-forsyning/features/opgavestyring").then((module) => module.EForsyningFeaturesOpgavestyringModule),
        canActivate: [SequentialGuards],
        data: {
            sequentialGuards: [AuthGuard, MenuBegraensningGuard],
            title: "Opgaver"
        }
    },
    {
        path: "mine-dokumenter",
        loadChildren: () => import("@e-forsyning/features/dokumenter").then((module) => module.EForsyningFeaturesDokumenterModule),
        canActivate: [SequentialGuards],
        data: {
            sequentialGuards: [AuthGuard, MenuBegraensningGuard],
            title: "Mine dokumenter"
        }
    },
    {
        path: "fakturagodkendelse",
        loadChildren: () => import("@e-forsyning/features/fakturagodkendelse").then((module) => module.EForsyningFeaturesFakturagodkendelseModule),
        canActivate: [SequentialGuards],
        data: {
            sequentialGuards: [AuthGuard, MenuBegraensningGuard],
            title: "Fakturagodkendelse"
        }
    },
    {
        path: "indtast-aflaesninger",
        loadChildren: () => import("@e-forsyning/features/maaler-aflaesning").then((module) => module.EForsyningFeaturesMaalerAflaesningModule),
        canActivate: [SequentialGuards],
        data: {
            sequentialGuards: [AuthGuard, MenuBegraensningGuard, InstallationGuard],
            title: "Indtast aflæsninger"
        }
    },
    {
        path: "meld-flytning",
        loadChildren: () => import("@e-forsyning/features/flyttemeddelelse").then((module) => module.EForsyningFeaturesFlyttemeddelelseModule),
        canActivate: [SequentialGuards],
        data: {
            sequentialGuards: [AuthGuard, MenuBegraensningGuard, InstallationGuard],
            title: "Meld flytning"
        }
    },
    {
        path: "mine-oplysninger",
        loadChildren: () => import("@e-forsyning/features/mine-oplysninger").then((module) => module.EForsyningFeaturesMineOplysningerModule),
        canActivate: [SequentialGuards],
        data: {
            sequentialGuards: [AuthGuard, MenuBegraensningGuard],
            title: "Mine oplysninger"
        }
    },
    {
        path: "oversigtskort",
        loadChildren: () => import("@e-forsyning/features/adresse-koordinater").then((module) => module.EForsyningFeaturesAdresseKoordinaterModule),
        canActivate: [SequentialGuards],
        data: {
            sequentialGuards: [AuthGuard, MenuBegraensningGuard, InstallationGuard],
            title: "Oversigtskort"
        }
    },
    {
        path: "kontakt",
        loadChildren: () => import("@e-forsyning/features/kontakt").then((module) => module.EForsyningFeaturesKontaktModule),
        canActivate: [SequentialGuards],
        data: {
            sequentialGuards: [AuthGuard, MenuBegraensningGuard],
            title: "Kontakt"
        }
    },
    {
        path: "mit-forbrug",
        loadChildren: () => import("@e-forsyning/features/mit-forbrug").then((module) => module.EForsyningFeaturesMitForbrugModule),
        canActivate: [SequentialGuards],
        data: {
            sequentialGuards: [AuthGuard, MenuBegraensningGuard, InstallationGuard],
            title: "Mit forbrug"
        }
    },
    {
        path: "sms-service",
        loadChildren: () => import("@e-forsyning/features/sms-service").then((module) => module.EForsyningFeaturesSmsServiceModule),
        canActivate: [SequentialGuards],
        data: {
            sequentialGuards: [AuthGuard, MenuBegraensningGuard],
            title: "SMS Service"
        }
    },
    {
        path: "forbrugsoverblik",
        loadChildren: () => import("@e-forsyning/features/forbrugsnotifikation").then((module) => module.EForsyningFeaturesForbrugsnotifikationModule),
        canActivate: [SequentialGuards],
        data: {
            sequentialGuards: [AuthGuard, MenuBegraensningGuard],
            title: "Forbrugsoverblik"
        }
    },
    //   {
    //       path: "forbrugsoverblik",
    //       redirectTo: "forbrugsnotifikation"
    //  },
    {
        path: "beregnet-varmeregnskab",
        loadChildren: () => import("@e-forsyning/features/prognose").then((module) => module.EForsyningFeaturesPrognoseModule),
        canActivate: [SequentialGuards],
        data: {
            sequentialGuards: [AuthGuard, MenuBegraensningGuard, InstallationGuard],
            title: "Beregnet varmeregnskab"
        }
    },
    // {
    //path: "beregnet-varmeregnskab",
    //redirectTo: "prognose"
    //  },
    {
        path: "sammenlign-forbrug",
        loadChildren: () => import("@e-forsyning/features/sammenlign-forbrug").then((module) => module.EForsyningFeaturesSammenlignForbrugModule),
        canActivate: [SequentialGuards],
        data: {
            sequentialGuards: [AuthGuard, MenuBegraensningGuard, InstallationGuard],
            title: "Sammenlign forbrug"
        }
    },
    {
        path: "min-side",
        loadChildren: () => import("@e-forsyning/features/min-side").then((module) => module.EForsyningFeaturesMinSideModule)
        // canActivate: [SequentialGuards],
        // data: {
        //     sequentialGuards: [AuthGuard, MenuBegraensningGuard],
        //     title: "Min side"
        // }
    },
    {
        path: "api/viewDocument/documentid/:dokumentId/unr/:ejendomNr/anr/:aktivNr/inr/:installationNr/viewkey/:viewkey",
        loadChildren: () => import("@e-forsyning/features/dokument-redirect").then((module) => module.EForsyningFeaturesDokumentRedirectModule)
    },
    {
        path: "**",
        loadChildren: () => import("@e-forsyning/features/error404").then((module) => module.EForsyningFeaturesError404Module)
    }
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes, {
            useHash: true,
            preloadingStrategy: PreloadAllModules,
            // TODO 14420: relativeLinkResolution = legacy is deprecated, but is an alternative needed?
            //relativeLinkResolution: "legacy",
            enableTracing: false
        })
    ],
    exports: [RouterModule]
})
export class AppRoutingModule {
    constructor(router: Router) {
        router.events.subscribe((e) => {
            // using `instanceof` you can filter the events of interest
            //if (e instanceof NavigationCancel) debugger;
        });
    }
}
